import { Button } from '@getro/rombo';
import React, { useMemo } from 'react';
import { Box, Flex } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Check, MinusCircle, Plus } from 'lucide-react';
import { useAddContactToSharedListMutation, useRemoveContactFromSharedListMutation } from 'services/contacts';
import { trackEvent } from 'helpers/analytics';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';

const AddContactsToList = ({ page, queryParams, contact, list, fullContactView, view, buttonProps }) => {
  const [addContactToSharedList, { isLoading: isLoadingAddContact }] = useAddContactToSharedListMutation();
  const [removeContactFromSharedList, { isLoading: isLoadingRemoveContact }] = useRemoveContactFromSharedListMutation();
  const { id } = useSelector(activeNetworkSelector);
  const contactInList = list?.id && contact?.sharedList?.some((item) => item.id === parseInt(list.id, 10));

  const buttonColor = useMemo(() => {
    if (view === 'table') {
      return contactInList ? 'text.main' : 'purple.500';
    }
    return contactInList ? 'purple.500' : 'white';
  }, [view, contactInList]);

  const onAddContactToList = async () => {
    trackEvent('contacts:list:add_contact_single', {
      listId: list?.id,
      collectionId: id,
      origin: fullContactView ? 'full_profile_cta' : 'card_cta',
      page,
    });

    addContactToSharedList({
      queryParams,
      list: { id: parseInt(list.id, 10), name: list?.name },
      contactId: contact.id,
    });
  };

  const onRemoveContactToList = () => {
    trackEvent('contacts:list:remove_contact_single', {
      listId: parseInt(list.id, 10),
      collectionId: id,
      origin: fullContactView ? 'full_profile_cta' : 'card_cta',
      page,
    });

    removeContactFromSharedList({
      queryParams,
      list: { id: parseInt(list.id, 10), name: list?.name },
      contactId: contact.id,
    });
  };

  return (
    <Button
      size="small"
      iconProps={{
        size: '16px',
      }}
      sx={{
        '& > div': {
          borderTopColor: 'purple.500',
        },
        ':hover': {
          '[data-testid="added-to-list"]': { display: 'none' },
          '[data-testid="remove-from-list"]': { display: 'flex' },
        },
      }}
      iconGap="8px"
      color={buttonColor}
      display="flex"
      justifyContent="center"
      width="100%"
      variant={contactInList ? 'tertiary' : 'primary'}
      loading={isLoadingAddContact || isLoadingRemoveContact}
      {...buttonProps}
      onClick={() => {
        if (contactInList) {
          onRemoveContactToList();
        } else {
          onAddContactToList();
        }
      }}
    >
      {contactInList && (
        <>
          <Flex data-testid="added-to-list" alignItems="center" sx={{ gap: '8px' }}>
            <Box as={Check} width="16px" height="16px" aria-hidden="true" />
            <Box as="p">Added</Box>
          </Flex>
          <Box data-testid="remove-from-list" display="none" alignItems="center" sx={{ gap: '8px' }}>
            <Box as={MinusCircle} width="16px" height="16px" aria-hidden="true" />
            <Box as="p">Remove from list</Box>
          </Box>
        </>
      )}
      {!contactInList && (
        <Flex alignItems="center" sx={{ gap: '8px' }}>
          {view === 'list' && <Box as={Plus} width="16px" height="16px" aria-hidden="true" />}
          <Box as="p">Add to list</Box>
        </Flex>
      )}
    </Button>
  );
};

AddContactsToList.propTypes = {
  page: PropTypes.string,
  queryParams: PropTypes.object.isRequired,
  contact: PropTypes.object.isRequired,
  list: PropTypes.object.isRequired,
  fullContactView: PropTypes.bool,
  view: PropTypes.string,
  buttonProps: PropTypes.object,
};
AddContactsToList.defaultProps = {
  page: '',
  fullContactView: false,
  view: 'list',
  buttonProps: {},
};

export default AddContactsToList;
